import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FaEye, FaRegUser } from "react-icons/fa";
import DataTable, { Column, Row } from "components/DataTable";
import HeaderButtons from "components/Button/HeaderButtons";
import { useToaster } from "contexts/toasterContext";
import { getApiErrorMessage } from "util/errorMessage";
import feedbackManager from "services/Feedback/feedbackManager";
import {
  DEFAULT_PAGE_SIZE,
  FeedbackSummary,
  formatFeedbackDate,
  getCurrentQuarter,
} from "services/Feedback/feedbackInterface";
// NOTE: reused from the legacy review screen rather than duplicated — it
// already derives a stable per-person colour from the name and handles an
// email-only row. If ReviewList is removed when the old data is migrated, move
// this Avatar into `components/` rather than copying it here.
import { Avatar } from "../ReviewList";
// NOTE: the same shared truncating cell the employee list uses, so both
// tables clip a long name or address identically and both keep the full
// value available on hover.
import { TruncatedText } from "pages/AdminConfig/components/TruncatedText";
import {
  TRUNCATION_REFERENCE_CLS,
  TRUNCATION_REFERENCE_TEXT,
  useNameColumnWidth,
} from "util/truncationWidth";
import { FeedbackTarget } from "./ManagerFeedbackForm";

/**
 * Manager / admin list: ONE ROW PER EMPLOYEE, not one row per feedback.
 *
 * NOTE: this is the load-bearing decision of the screen. With 40 employees
 * writing a sheet each quarter and several reviewers each adding feedback, a
 * row-per-record table spreads one person's history across pages and can never
 * answer the only question an admin actually asks — "how is this person
 * doing?". `GET /summary` already aggregates per person, so the list shows the
 * roll-up and the detail is one click deeper.
 */
export interface FeedbackSummaryListProps {
  /** Opens one employee's screen. */
  onSelectEmployee: (target: FeedbackTarget) => void;
  /**
   * CHANGE: opens the viewer's OWN screen, where they can raise their own
   * quarterly review request.
   *
   * NOTE: optional, so the list stays usable anywhere the caller has no "own
   * screen" to switch to. The button is rendered only when it is supplied.
   */
  onViewOwnFeedback?: () => void;
  heightClass?: string;
}

/** DataTable requires an `id`; the API's per-person key is `userId`. */
interface SummaryRow extends FeedbackSummary, Row {
  id: number;
}

/** Years offered in the dropdown: the current one and the three before it. */
const YEAR_WINDOW = 4;

const FeedbackSummaryList: React.FC<FeedbackSummaryListProps> = ({
  onSelectEmployee,
  onViewOwnFeedback,
  heightClass,
}) => {
  const { addToast } = useToaster();

  /*
   * The cap that actually produces the ellipsis. The w-[...] classes on
   * TruncatedText below are NOT what clips: lg:w-[350px] is more room than
   * almost any name or address needs, so on a wide screen nothing ever reaches
   * the ellipsis. This measured maxWidth sits on the parent and clamps them
   * down — it is the same mechanism, and the same number, the employee list
   * uses. See util/truncationWidth.
   */
  const { referenceRef, maxWidth: nameMaxWidth } = useNameColumnWidth();

  const currentYear = useMemo(() => getCurrentQuarter().year, []);
  const yearOptions = useMemo(
    () => Array.from({ length: YEAR_WINDOW }, (_, index) => currentYear - index),
    [currentYear],
  );

  const [rows, setRows] = useState<SummaryRow[]>([]);
  const [year, setYear] = useState(currentYear);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [totalItems, setTotalItems] = useState(0);
  const [loading, setLoading] = useState(false);

  const loadSummary = useCallback(async () => {
    setLoading(true);
    try {
      const response = await feedbackManager.getSummary({ year, search, page, size: pageSize });
      const payload = response.data;
      const content = Array.isArray(payload?.content) ? payload.content : [];

      setRows(content.map((item) => ({ ...item, id: item.userId })));
      setTotalItems(
        typeof payload?.totalElements === "number" ? payload.totalElements : content.length,
      );
    } catch (error) {
      addToast(getApiErrorMessage(error, "Failed to load the feedback summary"), "error");
      setRows([]);
      setTotalItems(0);
    } finally {
      setLoading(false);
    }
  }, [year, search, page, pageSize, addToast]);

  useEffect(() => {
    void loadSummary();
  }, [loadSummary]);

  // Changing the year or the search term must restart paging.
  const handleYearChange = (next: number) => {
    setYear(next);
    setPage(0);
  };

  const handleSearchChange = useCallback((term: string) => {
    setSearch((previous) => (previous === term ? previous : term));
    setPage(0);
  }, []);

  const handlePageChange = useCallback((next: number) => setPage(next - 1), []);

  const handlePageSizeChange = useCallback((next: number) => {
    setPageSize((previous) => (previous === next ? previous : next));
    setPage(0);
  }, []);

  /** How many people on THIS page are waiting on a reviewer. */
  const pendingOnPage = rows.filter((row) => row.pendingReviewCount > 0).length;

  const columns: Column[] = [
    {
      key: "name",
      label: "Employee",
      bold: true,

      render: (_value, row) => {
        const summary = row as SummaryRow;
        const displayName = summary.name || summary.email.split("@")[0];

        /**
         * CHANGE: both lines go through `TruncatedText` with the SAME
         * responsive widths the employee list uses (EmployeeList.tsx:491),
         * rather than a bare `truncate` class. `truncate` is overflow-hidden +
         * ellipsis and does nothing until something gives it a definite width,
         * which this cell never had — so a long name or address widened the
         * column and pushed the rest of the table sideways.
         *
         * NOTE: `TruncatedText` also sets `title`, which is the rule
         * util/truncate states — a shortened value must stay readable on hover.
         * DataTable normally applies that itself through `capContent`, but only
         * to plain string/number cells; a column with a `render` returns JSX,
         * which `capContent` passes straight through, so a custom-rendered
         * column has to honour the rule directly.
         *
         * NOTE: unlike the base -> sm: pairs this codebase has trouble with,
         * these widths DO apply: the base `w-[200px]` is emitted at byte 8860
         * of the built stylesheet and the sm:/lg: overrides at 176185/182849,
         * so the responsive rules come later and win.
         *
         * NOTE: EmployeeList additionally measures a hidden reference span for
         * a pixel cap. Not copied — the width classes are what actually clip
         * the text, and the measured value is the inert part of that pattern
         * (its email counterpart is computed and never read).
         */
        return (
          <div className="flex items-center gap-2.5">
            <Avatar name={summary.name || summary.email} />

            {/* min-w-0 lets the text block shrink below its content width
                inside the flex row; Avatar is already flex-shrink-0. The
                measured maxWidth is what clamps the w-[...] widths below to a
                size a real name actually exceeds. */}
            <div
              className="flex min-w-0 flex-col"
              style={{ maxWidth: nameMaxWidth ? `${nameMaxWidth}px` : "200px" }}
            >
              <TruncatedText
                text={displayName}
                className="w-[200px] sm:w-[250px] lg:w-[350px] text-sm font-medium text-gray-900 dark:text-white leading-tight"
              />

              <TruncatedText
                text={summary.email}
                className="w-[200px] sm:w-[250px] lg:w-[350px] text-xs text-gray-400 dark:text-gray-500 leading-tight mt-0.5"
              />
            </div>
          </div>
        );
      },
    },

    {
      key: "pendingReviewCount",
      label: "Needs review",
      render: (_value, row) => {
        const summary = row as SummaryRow;
        // Amber only when there is something to act on, so a scan of the column
        // picks out the actionable rows without reading every number.
        return summary.pendingReviewCount > 0 ? (
          <span className="inline-flex items-center rounded-full bg-amber-50 px-2.5 py-0.5 text-xs font-semibold tabular-nums text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">
            {summary.pendingReviewCount} waiting
          </span>
        ) : (
          <span className="text-xs text-gray-400 dark:text-gray-500">—</span>
        );
      },
    },
    {
      key: "selfCount",
      label: "Goals",
      render: (_value, row) => (
        <span className="text-sm tabular-nums text-gray-700 dark:text-gray-300">
          {(row as SummaryRow).selfCount}
        </span>
      ),
    },
    {
      key: "receivedCount",
      label: "Reviews",
      render: (_value, row) => (
        <span className="text-sm tabular-nums text-gray-700 dark:text-gray-300">
          {(row as SummaryRow).receivedCount}
        </span>
      ),
    },
    {
      key: "lastFeedbackAt",
      label: "activity",
      render: (_value, row) => (
        <span className="text-sm text-gray-600 dark:text-gray-300">
          {formatFeedbackDate((row as SummaryRow).lastFeedbackAt)}
        </span>
      ),
    },
    {
      key: "actions",
      label: "Actions",
      excludeExport: true,
      actions: (row: Row) => {
        const summary = row as SummaryRow;

        /*
         * CHANGE: was a `HeaderButtons variant="tertiary"` reading "View".
         * Two problems. It did not match the app's table action button — the
         * employee, reimbursement and leave tables all render an icon in a
         * `btn-primary` box (DataTable.css: blue border and glyph, filling
         * blue on hover), so this column was the only one in the product with
         * a worded button. And `tertiary` applies
         * `backgroundColor: colors.white` as an INLINE style, which outranks
         * any `dark:` class, so in dark mode it was a white slab.
         *
         * `btn-primary` has no background of its own, so it reads correctly in
         * both themes — which is why every other table already uses it.
         *
         * NOTE: a <button>, not the <span onClick> those older tables use.
         * Same classes and same appearance, but reachable by keyboard; this is
         * already how FeedbackCard renders its own btn-primary actions.
         */
        return (
          <button
            type="button"
            onClick={() => onSelectEmployee({ userId: summary.userId, name: summary.name })}
            title="View review "
            aria-label={`View review for ${summary.name || summary.email}`}
            className="btn-primary cursor-pointer rounded-lg px-2 py-2"
          >
            <FaEye size={13} />
          </button>
        );
      },
    },
  ];

  const leftSideButtons = (
    <div className="flex flex-wrap items-center gap-2">
      {/* CHANGE: the way out of the roll-up and onto the viewer's own screen.
          NOTE: `h-[38px]` matches the year select beside it, so the two line up
          on the same baseline instead of the button sitting 4px short. */}
      {onViewOwnFeedback && (
        <HeaderButtons
          type="button"
          variant="tertiary"
          size="small"
          className="h-[38px]"
          icon={<FaRegUser size={11} />}
          onClick={onViewOwnFeedback}
        >
          My Reviews
        </HeaderButtons>
      )}

      <select
        value={year}
        onChange={(e) => handleYearChange(Number(e.target.value))}
        aria-label="Feedback year"
        disabled={loading}
        className="h-[38px] rounded-lg border border-gray-300 bg-white px-2.5 text-sm text-gray-900 focus:border-primary focus:ring-1 focus:ring-primary disabled:opacity-60 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
      >
        {yearOptions.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>

      {pendingOnPage > 0 && (
        <span className="inline-flex items-center rounded-lg bg-amber-50 px-2.5 py-1.5 text-xs font-medium text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">
          {pendingOnPage} on this page need review
        </span>
      )}
    </div>
  );

  return (
    <>
      {/* Off-screen reference the cap is measured from. It has to be in the
          live document because the width depends on the font the browser
          actually resolves, which cannot be computed ahead of time. */}
      <div className="absolute invisible pointer-events-none whitespace-nowrap">
        <span ref={referenceRef} className={TRUNCATION_REFERENCE_CLS}>
          {TRUNCATION_REFERENCE_TEXT}
        </span>
      </div>

      <DataTable
        heightClass={heightClass}
        data={rows}
        columns={columns}
        loading={loading}
        leftSideButtons={leftSideButtons}
        serverSidePagination
        currentPage={page + 1}
        totalItems={totalItems}
        itemsPerPage={pageSize}
        onPageChange={handlePageChange}
        onItemsPerPageChange={handlePageSizeChange}
        onSearchTermChange={handleSearchChange}
        searchDebounceMs={500}
        searchPlaceholder="Search name or email"
        searchAutoComplete="off"
        isExportable={false}
      />
    </>
  );
};

export default FeedbackSummaryList;
